import {
  FEATURE_ACCESS_PARAMETER_KEY,
  isFeatureAccessConfig,
} from '@diamond/shared/schema/feature-access';
import type {
  FeatureAccessConfig,
  FeatureAccessKey,
} from '@diamond/shared/types';
import type { UseFeatureFlagReturn } from '@diamond/shared/utils';
import {
  buyerCaptureException,
  FEATURE_ACCESS_FLAGS,
  getRemoteConfigJSON,
  hasFeature,
  normalizeFeatureAccess,
  useFeatureFlagWithState,
} from '@diamond/shared/utils';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';

import { useAccountMeta } from './hooks';
import { useShipToStore } from './stores';

const FEATURE_ACCESS_ERROR_CODE = 'FAC-001';

export interface FeatureAccessState {
  canUse: boolean;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;
}

interface FeatureAccessContextValue {
  getFeatureAccess: (featureKey: FeatureAccessKey) => FeatureAccessState;
}

type FeatureFlags = Record<FeatureAccessKey, UseFeatureFlagReturn>;

interface FeatureAccessShipTo {
  segments?: unknown;
}

interface FeatureAccessErrorParams {
  accountError: unknown;
  canEvaluateShipTo: boolean;
  featureFlags: FeatureFlags;
  hasInvalidSelectedShipToSegments: boolean;
  hasLastValidConfig: boolean;
  isAccountError: boolean;
  isAccountLoading: boolean;
  remoteConfig: unknown;
  selectedShipTo?: FeatureAccessShipTo;
}

interface FeatureAccessError {
  message: string;
  source: string;
}

const FeatureAccessContext = createContext<FeatureAccessContextValue | null>(
  null
);

function getNormalizedFeatureAccess(
  config: unknown,
  selectedShipToSegments: string[] | null
) {
  if (selectedShipToSegments === null) return null;
  if (!isFeatureAccessConfig(config)) return null;

  return normalizeFeatureAccess(config, selectedShipToSegments);
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.every((segment: unknown) => typeof segment === 'string')
  );
}

function getFeatureAccessSegments(value: unknown): string[] | null {
  if (value == null) return [];
  return isStringArray(value) ? value : null;
}

function getFeatureAccessError({
  accountError,
  canEvaluateShipTo,
  featureFlags,
  hasInvalidSelectedShipToSegments,
  hasLastValidConfig,
  isAccountError,
  isAccountLoading,
  remoteConfig,
  selectedShipTo,
}: FeatureAccessErrorParams): FeatureAccessError | null {
  if (!canEvaluateShipTo) return null;
  if (isAccountError) {
    return {
      source: 'account_meta',
      message:
        accountError instanceof Error
          ? accountError.message
          : 'Failed to load account metadata',
    };
  }

  for (const [featureKey, flag] of Object.entries(featureFlags)) {
    if (flag.error) {
      return { source: `${featureKey}_flag`, message: flag.error };
    }
  }

  if (!isAccountLoading && !selectedShipTo) {
    return {
      source: 'selected_shipto_missing',
      message: 'Selected ship-to is missing from account metadata',
    };
  }
  if (hasInvalidSelectedShipToSegments) {
    return {
      source: 'shipto_segments_invalid',
      message: 'Selected ship-to segments are invalid',
    };
  }

  const isFlagLoading = Object.values(featureFlags).some(
    (flag) => flag.loading
  );
  if (isFlagLoading) return null;

  if (remoteConfig === null && !hasLastValidConfig) {
    return {
      source: 'feature_access_config_missing',
      message: 'Feature access config is missing',
    };
  }
  if (remoteConfig !== null && !isFeatureAccessConfig(remoteConfig)) {
    return {
      source: 'feature_access_config_invalid',
      message: 'Feature access config is invalid',
    };
  }

  return null;
}

function useFeatureAccessErrorReporter(
  error: FeatureAccessError | null,
  selectedShipToId: string
) {
  const lastReportedErrorKey = useRef<string | null>(null);
  const errorMessage = error?.message;
  const errorSource = error?.source;
  const errorKey =
    errorMessage && errorSource
      ? `${selectedShipToId}:${errorSource}:${errorMessage}`
      : null;

  useEffect(() => {
    if (!errorKey || !errorMessage || !errorSource) {
      lastReportedErrorKey.current = null;
      return;
    }
    if (lastReportedErrorKey.current === errorKey) return;

    lastReportedErrorKey.current = errorKey;

    buyerCaptureException(
      {
        message: errorMessage,
        error_code: FEATURE_ACCESS_ERROR_CODE,
        source: errorSource,
      },
      (scope) => {
        scope.setTag('feature', 'feature-access');
        scope.setTag('error_code', FEATURE_ACCESS_ERROR_CODE);
        scope.setTag('error_source', errorSource);
        scope.setContext('feature_access', {
          selectedShipToId,
          errorMessage,
        });
      }
    );
  }, [errorKey, errorMessage, errorSource, selectedShipToId]);
}

export function FeatureAccessProvider({ children }: { children: ReactNode }) {
  const {
    data: account,
    error: accountError,
    isError: isAccountError,
    isPending: isAccountLoading,
  } = useAccountMeta();
  const selectedShipToId = useShipToStore((state) => state.selectedShipToId);
  const isShipToStoreHydrated =
    typeof window !== 'undefined' &&
    (useShipToStore.persist?.hasHydrated?.() ?? false);
  const lastValidConfig = useRef<FeatureAccessConfig | null>(null);

  const cartRecommendationFlag = useFeatureFlagWithState(
    FEATURE_ACCESS_FLAGS.cart_recommendation,
    false
  );
  const paymentProofOcrFlag = useFeatureFlagWithState(
    FEATURE_ACCESS_FLAGS.payment_proof_ocr,
    false
  );
  const homepageRecipeFlag = useFeatureFlagWithState(
    FEATURE_ACCESS_FLAGS.homepage_recipe,
    false
  );
  const poDetailsDataFlag = useFeatureFlagWithState(
    FEATURE_ACCESS_FLAGS.po_details_data,
    true
  );

  const selectedShipTo = account?.ship_tos.find(
    (shipTo) => shipTo.id === selectedShipToId
  );
  const remoteConfig = getRemoteConfigJSON<unknown>(
    FEATURE_ACCESS_PARAMETER_KEY,
    null
  );

  useEffect(() => {
    if (isFeatureAccessConfig(remoteConfig)) {
      lastValidConfig.current = remoteConfig;
    }
  }, [remoteConfig]);

  const config = remoteConfig === null ? lastValidConfig.current : remoteConfig;

  const featureFlags = {
    cart_recommendation: cartRecommendationFlag,
    payment_proof_ocr: paymentProofOcrFlag,
    homepage_recipe: homepageRecipeFlag,
    po_details_data: poDetailsDataFlag,
  } satisfies FeatureFlags;

  const selectedShipToSegments = selectedShipTo
    ? getFeatureAccessSegments(selectedShipTo.segments)
    : null;
  const hasInvalidSelectedShipToSegments =
    selectedShipTo !== undefined && selectedShipToSegments === null;
  const normalized = getNormalizedFeatureAccess(
    config,
    selectedShipToSegments
  );

  const canEvaluateShipTo =
    isShipToStoreHydrated && selectedShipToId.length > 0;

  const featureAccessError = getFeatureAccessError({
    accountError,
    canEvaluateShipTo,
    featureFlags,
    hasInvalidSelectedShipToSegments,
    hasLastValidConfig: lastValidConfig.current !== null,
    isAccountError,
    isAccountLoading,
    remoteConfig,
    selectedShipTo,
  });

  useFeatureAccessErrorReporter(featureAccessError, selectedShipToId);

  const isProviderLoading = isAccountLoading || !isShipToStoreHydrated;

  const getFeatureAccess = (featureKey: FeatureAccessKey) => {
    const featureFlag = featureFlags[featureKey];

    const isLoading = isProviderLoading || featureFlag.loading;

    const hasAccessDataError =
      canEvaluateShipTo && !isLoading && normalized === null;

    const isError =
      isAccountError || featureFlag.error !== null || hasAccessDataError;

    const isReady = canEvaluateShipTo && !isLoading && !isError;

    const canUse = hasFeature({
      featureKey,
      flagEnabled: featureFlag.value,
      flagLoading: !isReady,
      normalized,
    });

    return { canUse, isReady, isLoading, isError };
  };

  return (
    <FeatureAccessContext.Provider value={{ getFeatureAccess }}>
      {children}
    </FeatureAccessContext.Provider>
  );
}

export function useFeatureAccess(
  featureKey: FeatureAccessKey
): FeatureAccessState {
  const context = useContext(FeatureAccessContext);

  if (!context) {
    throw new Error(
      'useFeatureAccess must be used within FeatureAccessProvider'
    );
  }

  return context.getFeatureAccess(featureKey);
}
